import { Box, CircularProgress, Dialog, Grid } from '@material-ui/core'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { ReactComponent as PtStockIcon } from 'img/ptStock.svg'
import { useDialog } from 'lib/hooks'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { GAME_IDS, generateArticleKey, InventoryArticle, isSingle } from 'shared'
import { CompleteDataInGradedCards } from './CompleteDataInGradedCards'
import { GameStockBox } from './GameStockBox'
import { SquareButton } from './SquareButton'
import { StockReviewFull } from './StockReviewFull'

interface StockReviewFixGradeProps {
  workingIncomingStockBeforeUpdatinGrade: InventoryArticle[]
  setWorkingIncomingStockBeforeUpdatinGrade: React.Dispatch<
    React.SetStateAction<InventoryArticle[]>
  >
  fillSortedIncomingStock: (newIncomingStock: InventoryArticle[]) => void
  onGradesCompleted: () => void
}

export const StockReviewFixGrade = ({
  workingIncomingStockBeforeUpdatinGrade,
  setWorkingIncomingStockBeforeUpdatinGrade,
  fillSortedIncomingStock,
  onGradesCompleted,
}: StockReviewFixGradeProps): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const history = useHistory()
  const [setLoadingDialogOpen, LoadingDialog] = useDialog()

  const [fullListIdGame, setFullListIdGame] = useState(0)
  const [fullListType, setFullListType] = useState<'singles' | 'accessories'>('singles')
  const [showFullList, setShowFullList] = useState(false)
  const [commentPresent, setCommentsPresent] = useState(false)
  const [openDialogGradedCards, setOpenDialogGradedCards] = useState(false)
  const isFirstImport = user?.ptInventory?.lastManualSyncWithCardmarketAt === null

  // const isFirstImport = user?.ptInventory?.lastManualSyncWithCardmarketAt === null

  const GAME_IDS_WITH_GENERIC = [0].concat(GAME_IDS)

  const defaultCounters: {
    [idGame: number]: {
      singles: number
      uniqueSingles: number
      sealed: number
      uniqueSealed: number
      totalUnique: number
    }
  } = {}
  Object.values(GAME_IDS_WITH_GENERIC).forEach((idGame) => {
    defaultCounters[idGame] = {
      singles: 0,
      uniqueSingles: 0,
      sealed: 0,
      uniqueSealed: 0,
      totalUnique: 0,
    }
  })
  const [counters, setCounters] = useState<{
    [idGame: number]: {
      singles: number
      uniqueSingles: number
      sealed: number
      uniqueSealed: number
      totalUnique: number
    }
  }>(defaultCounters)

  const handleShowFullList = (idGame: number, type: 'singles' | 'accessories') => {
    setFullListIdGame(idGame)
    setFullListType(type)
    setShowFullList(true)
  }

  useEffect(() => {
    if (workingIncomingStockBeforeUpdatinGrade.length !== 0) {
      const newCounters = { ...counters }
      // Cardmarket has one listing per price and comment: listings of the same product count once, as the
      // product they become on Powertools. The copies are the ones on Cardmarket, listing by listing
      const countedProducts = new Set<string>()
      workingIncomingStockBeforeUpdatinGrade.forEach((article) => {
        const idGame = article.card.idGame
        if (idGame === undefined) return

        const productKey = generateArticleKey(article)
        const isNewProduct = !countedProducts.has(productKey)
        countedProducts.add(productKey)

        if (isSingle(article.card)) {
          newCounters[idGame].singles += article.quantity
          if (isNewProduct) {
            newCounters[idGame].uniqueSingles += 1
            newCounters[idGame].totalUnique += 1
          }
        } else {
          newCounters[idGame].sealed += article.quantity
          if (isNewProduct) {
            newCounters[idGame].uniqueSealed += 1
            newCounters[idGame].totalUnique += 1
          }
        }

        if (article.comment && commentPresent === false) {
          setCommentsPresent(true)
        }
      })
      setCounters(newCounters)
    }
    setLoadingDialogOpen(false)
  }, [workingIncomingStockBeforeUpdatinGrade])

  /**
   *
   * confirm the grade set on each card
   */
  const handleConfirmGradedCards = (newIncomingStock: InventoryArticle[]) => {
    setWorkingIncomingStockBeforeUpdatinGrade(newIncomingStock)
    setOpenDialogGradedCards(false)

    // Signal that grades were just completed here, so the StockReview screen we're about to fall through
    // to can auto-advance instead of showing a second, identical recap that the user must click again.
    onGradesCompleted()
    fillSortedIncomingStock(newIncomingStock)
  }

  /**
   * if all cards have the grade,
   * tells SyncInventoryPresenter that the data is ready and it can do something else, typically proceed to fill sortedIncomingStock
   */
  const handleStartSyncing = () => {
    const cardsWithoutGrade = workingIncomingStockBeforeUpdatinGrade.filter(
      (article) => article.gradingCompany && article.grade === undefined
    ).length

    if (cardsWithoutGrade > 0) setOpenDialogGradedCards(true)
    else {
      fillSortedIncomingStock(workingIncomingStockBeforeUpdatinGrade)
    }
  }

  return (
    <>
      {workingIncomingStockBeforeUpdatinGrade.length > 0 ? (
        <>
          <Box>
            <Box className="title" marginBottom={5}>
              <div className="titleFont">{t('ptInventory.stockReview.title')}</div>
              <h3>{t('ptInventory.stockReview.addMissingGrade')}</h3>
            </Box>

            <Grid container spacing={1}>
              {GAME_IDS_WITH_GENERIC.map((gameId) => {
                return (
                  <Fragment key={`gameBox-${gameId}`}>
                    {counters[gameId].totalUnique > 0 && (
                      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <GameStockBox
                          idGame={gameId}
                          counters={counters[gameId]}
                          handleShowFullList={handleShowFullList}
                        />
                      </Grid>
                    )}
                  </Fragment>
                )
              })}
            </Grid>
            <Box marginY={5} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
                <Box className="inventoryButtonContainer">
                  <Button
                    data-testid="start-import-btn"
                    variant="contained"
                    className="inventoryButton"
                    onClick={handleStartSyncing}
                  >
                    {isFirstImport
                      ? t('ptInventory.stockReview.startImport.firstTimeImport')
                      : t('ptInventory.stockReview.startImport.notFirstTimeImport')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {openDialogGradedCards && (
            <Dialog open={openDialogGradedCards} maxWidth="lg">
              <CompleteDataInGradedCards
                incomingStock={workingIncomingStockBeforeUpdatinGrade}
                handleConfirmGradedCards={handleConfirmGradedCards}
              />
            </Dialog>
          )}

          <Dialog maxWidth="lg" open={showFullList} onClose={() => setShowFullList(false)}>
            <div className="p-4">
              <h3>{t('ptInventory.stockReview.completeReview')}</h3>
              <StockReviewFull
                incomingStock={workingIncomingStockBeforeUpdatinGrade}
                idGame={fullListIdGame}
                type={fullListType}
                handleClose={() => setShowFullList(false)}
              />
            </div>
          </Dialog>
        </>
      ) : (
        <>
          <h3>{t('ptInventory.stockReview.emptyCardmarketStock')}</h3>
          <Grid container spacing={2}>
            <Grid data-testid="go-to-stock-btn" item xs={1}>
              <SquareButton
                image={<PtStockIcon />}
                text={t('ptInventory.welcome.goToStock')}
                type="secondary"
                handleClick={() => history.push('/stock-pricing')}
              />
            </Grid>
          </Grid>
        </>
      )}

      <LoadingDialog
        title="Loading..."
        content={
          <Box marginTop={3} height="8rem" width="fit-content" margin="0 auto">
            <CircularProgress size={100} />
          </Box>
        }
      />
    </>
  )
}
